import { getLeaderboard } from './leaderboard.requests.js';
import { LeaderboardAuthService } from './leaderboard_auth_service.js';
import { LeaderboardSyncService } from './leaderboard_sync_service.js';
import { GLOBAL_BOARD_ID, countryBoardId, playerCountry } from './leaderboard_country.js';
import { compactUsers, headTop, packBoard, unpackBoard } from './leaderboard_board_codec.js';
import type { BoardUser } from './leaderboard.dto.js';
import { PersistentCache } from '../../offline/persistent_cache.js';
import { GlobalSettings } from '../../global_settings.js';
import { NetworkManager } from '../../network_manager.js';

/** How many players the board asks for (the server adds the current player's row when it is lower). */
export const LEADERBOARD_LIMIT = 1000;
/** First request when there is nothing to show yet: a screenful and some, ~80 ms instead of ~250. */
const HEAD_LIMIT = 49;
/** A board fetched this recently is reused, unless the player's own score or profile changed. */
const REFRESH_MIN_MS = 60_000;
/**
 * The menu prefetch skips a board fetched in this session less than this long ago, even if the
 * player's score changed since: the menu comes back after every level, and the open itself still
 * refreshes such a board in the background (the player's own row updates in place).
 */
const PREFETCH_MIN_MS = 5 * 60_000;
/** The previous session's board is shown while the fresh one loads, if it is not older than this. */
const PERSISTED_MAX_AGE_MS = 24 * 60 * 60 * 1000;

export interface BoardSnapshot
{
  users: BoardUser[];
  /** False while only the top HEAD_LIMIT is in and the full board is still loading. */
  complete: boolean;
  /** Fetched in this session; false for the board restored from the previous one. */
  live: boolean;
  fetched_at: number;
}

type Listener = ( snapshot: BoardSnapshot ) => void;

export interface BoardSource
{
  /** Best board available right now: this session's, else the previous session's (≤ 24 h), else null. */
  get(): BoardSnapshot | null;
  /** Called with every new board (head, then full). Returns the unsubscribe function. */
  subscribe( listener: Listener ): () => void;
  isFresh(): boolean;
  isLoading(): boolean;
  /** Fetches the board; concurrent calls share one load. Rejects when the full board failed. */
  refresh(): Promise<void>;
  refreshIfStale(): Promise<void>;
  /**
   * Menu idle: logs in and loads the board before the player opens it, so the open needs no
   * request at all. Respects the refresh throttle, never rejects.
   */
  prefetch(): Promise<void>;
  /** Debug: drops the in-memory and persisted board. */
  reset(): void;
}

interface BoardSourceOptions
{
  /** Board id on the server; null when this board cannot be shown (no country known). */
  id: () => string | null;
  persist_key: string;
  /** Resolves when the player's own row on this board is up to date; the full list waits for it. */
  ready: () => Promise<void>;
}

/**
 * Keeps the last board of one leaderboard id in memory and in localStorage, so opening the
 * screen shows a board at once and only refreshes it; the network is never on the path to the
 * first frame unless nothing is known yet.
 */
function createBoardSource( options: BoardSourceOptions ): BoardSource
{
  let snapshot: BoardSnapshot | null = null;
  /** Board id the snapshot belongs to: the country can change between sessions. */
  let snapshot_id: string | null = null;
  let restore_tried = false;
  let fetched: { at: number; revision: number; id: string } | null = null;
  let in_flight: Promise<void> | null = null;
  const listeners = new Set<Listener>();

  // The persisted board is tied to the backend and the board id: a QA environment override or
  // another country must not show the other board. The global board keeps the plain base URL,
  // so boards saved by older builds stay valid.
  const persistEnv = ( id: string ): string =>
    id === GLOBAL_BOARD_ID ? GlobalSettings.LEADERBOARD_BASE_URL : `${GlobalSettings.LEADERBOARD_BASE_URL}#${id}`;

  const publish = ( id: string, next: BoardSnapshot ): void =>
  {
    snapshot = next;
    snapshot_id = id;
    for ( const listener of [ ...listeners ] )
    {
      try { listener( next ); }
      catch ( e ) { console.warn( '[LeaderboardBoard] listener failed', e ); }
    }
  };

  const persist = ( id: string, users: BoardUser[], at: number ): void =>
  {
    // Off the frame that shows the new board: ~70 KB of JSON into localStorage.
    setTimeout( () => PersistentCache.write( options.persist_key, packBoard( users, at, persistEnv( id ) ) ), 0 );
  };

  const load = async ( id: string ): Promise<void> =>
  {
    // Auth only: board registration runs in the background (see LeaderboardAuthService.registered).
    await LeaderboardAuthService.ensureLoggedIn();
    const revision = LeaderboardSyncService.boardRevision();

    // The full board must contain the player's own row, so it waits for this session's registration.
    let full_done = false;
    const full = options.ready().then( () => getLeaderboard( id, LEADERBOARD_LIMIT ) );
    full.then( () => { full_done = true; }, () => { full_done = true; } );

    if ( !source.get()?.complete )
    {
      try
      {
        const head = await getLeaderboard( id, HEAD_LIMIT );
        if ( !full_done && !snapshot?.complete )
          publish( id, {
            users: compactUsers( headTop( head.users ?? [] ) ),
            complete: false,
            live: true,
            fetched_at: Date.now(),
          } );
      }
      catch ( e )
      {
        console.warn( `[LeaderboardBoard] ${id}: head request failed, waiting for the full board`, e );
      }
    }

    const res = await full;
    const users = compactUsers( res.users ?? [] );
    // The catch-up behind ready() can bump the revision: the board fetched after it is current.
    fetched = { at: Date.now(), revision: Math.max( revision, LeaderboardSyncService.boardRevision() ), id };
    publish( id, { users, complete: true, live: true, fetched_at: fetched.at } );
    persist( id, users, fetched.at );
  };

  const source: BoardSource = {
    get(): BoardSnapshot | null
    {
      const id = options.id();
      if ( id === null )
        return null;
      if ( snapshot && snapshot_id !== id )
      {
        snapshot = null;
        fetched = null;
      }
      if ( !snapshot && ( !restore_tried || snapshot_id !== id ) )
      {
        restore_tried = true;
        snapshot_id = id;
        const restored = unpackBoard(
          PersistentCache.read( options.persist_key ), persistEnv( id ), Date.now(), PERSISTED_MAX_AGE_MS
        );
        if ( restored )
          snapshot = { users: restored.users, complete: true, live: false, fetched_at: restored.at };
      }
      return snapshot;
    },

    subscribe( listener: Listener ): () => void
    {
      listeners.add( listener );
      return () => { listeners.delete( listener ); };
    },

    isFresh(): boolean
    {
      return fetched !== null
        && fetched.id === options.id()
        && Date.now() - fetched.at < REFRESH_MIN_MS
        && fetched.revision === LeaderboardSyncService.boardRevision();
    },

    isLoading(): boolean
    {
      return in_flight !== null;
    },

    refresh(): Promise<void>
    {
      const id = options.id();
      if ( id === null )
        return Promise.reject( new Error( '[LeaderboardBoard] no board id (country unknown)' ) );
      if ( !in_flight )
        in_flight = load( id ).finally( () => { in_flight = null; } );
      return in_flight;
    },

    refreshIfStale(): Promise<void>
    {
      if ( in_flight )
        return in_flight;
      return this.isFresh() ? Promise.resolve() : this.refresh();
    },

    async prefetch(): Promise<void>
    {
      if ( !NetworkManager.isOnline() || options.id() === null )
        return;
      if ( fetched !== null && fetched.id === options.id() && Date.now() - fetched.at < PREFETCH_MIN_MS )
        return;
      try
      {
        await this.refreshIfStale();
      }
      catch ( e )
      {
        console.warn( '[LeaderboardBoard] prefetch failed', e );
      }
    },

    reset(): void
    {
      snapshot = null;
      snapshot_id = null;
      fetched = null;
      restore_tried = true;
      PersistentCache.remove( options.persist_key );
    },
  };
  return source;
}

/** The global board (`global_0`): leaderboard screen's Global tab and the menu prefetch. */
export const LeaderboardBoardService: BoardSource = createBoardSource( {
  id: () => GLOBAL_BOARD_ID,
  persist_key: 'leaderboard_board_v1',
  ready: () => LeaderboardAuthService.registered(),
} );

/** The player's country board (`country:<code>`): the Country tab. Null id while the country is unknown. */
export const LeaderboardCountryBoardService: BoardSource = createBoardSource( {
  id: () =>
  {
    const country = playerCountry();
    return country ? countryBoardId( country ) : null;
  },
  persist_key: 'leaderboard_board_country_v1',
  ready: () => LeaderboardSyncService.countryReady(),
} );
